import { BaseButton, DefaultButton, Dialog, DialogContent, DialogFooter, DialogType, IButtonProps, IButtonStyles, IMessageBarStyles, MessageBar, MessageBarType, PrimaryButton, Stack, StackItem } from '@fluentui/react';
import * as React from 'react';

/**
 * @summary Defines the base properties for other dialog property interfaces.
 */
interface IBaseDialogProps {
    /**
     * @summary The title of the dialog.
     */
    title: string;
    /**
     * @summary The content of the dialog.
     */
    message: string | JSX.Element;
}

/**
 * @summary Defines the properties of the SimpleDialog.
 */
export interface ISimpleDialogProps extends IBaseDialogProps {
    /**
     * @summary The buttons to show in the dialog footer.
     */
    buttons: ISimpleDialogButtonProps[];
}

/**
 * @summary The types of buttons supported by the simple dialog.
 */
export enum SimpleDialogButtonType {
    /**
     * @summary Indicates that a Fluent UI PrimaryButton will be used.
     */
    Primary,
    /**
     * @summary Indicates that a Fluent UI DefaultButton will be used.
     */
    Default
}

/**
 * @summary Defines the properties of a message for the simple dialog.
 */
export interface ISimpleDialogMessageProps {
    /**
     * @summary The type of message.
     */
    type: MessageBarType;
    /**
     * @summary The text of the message.
     */
    text: string;
    /**
     * @summary The styles of the message bar.
     */
    styles?: IMessageBarStyles;
}

/**
 * @summary Defines the state of the SimpleDialog.
 */
export interface ISimpleDialogState {
    /**
     * @summary The messages to display in the simple dialog.
     */
    messages: ISimpleDialogMessageProps[];
}

/**
 * @summary Defines the properties of a button for the simple dialog.
 */
export interface ISimpleDialogButtonProps {
    /**
     * @summary The type of button.
     */
    type: SimpleDialogButtonType;
    /**
     * @summary The button text.
     */
    text: string;
    /**
     * @summary The button styles.
     */
    styles?: IButtonStyles;
    /**
     * @summary A function to call when the button is clicked.
     */
    onClick?: (event: React.MouseEvent<HTMLAnchorElement | HTMLButtonElement | HTMLDivElement | BaseButton | HTMLSpanElement, MouseEvent>) => ISimpleDialogMessageProps | void;
    /**
     * @summary The link to navigate to.
     */
    href?: string;
    /**
     * @summary The frame to target.
     */
    target?: "_blank" | "_self" | "_parent" | "_top" | string;
}

/**
 * @summary An abstraction of the Fluent UI Dialog that only requires a title, message and buttons.
 */
export class SimpleDialog extends React.Component<ISimpleDialogProps, ISimpleDialogState> {
    constructor(props: ISimpleDialogProps) {
        super(props);
        this.state = {
            messages: []
        }
    }

    private _onButtonClicked = (button: ISimpleDialogButtonProps, event: any) => {
        var buttonResult = typeof button.onClick === "function" && button.onClick(event);
        if (buttonResult) {
            this.setState({ messages: [...this.state.messages, buttonResult] });
        }
    };

    private _onMessageDismissed = (message: ISimpleDialogMessageProps) => {
        this.setState({ messages: this.state.messages.filter(m => m !== message) });
    };

    public render() {
        return (<>
            <Dialog
                hidden={false}
                dialogContentProps={{
                    type: DialogType.normal,
                    showCloseButton: false,
                    title: this.props.title
                }}
                modalProps={{
                    isBlocking: true,
                    styles: { main: { minWidth: 450, maxWidth: 450 } }
                }} >
                <Stack tokens={{ childrenGap: 20 }}>
                    <StackItem>{this.props.message}</StackItem>
                    {this.state.messages.length > 0 && <>
                        {this.state.messages.map((m, i) => (<StackItem key={`message-${i}`}>
                            <MessageBar styles={m.styles} messageBarType={m.type} onDismiss={() => { this._onMessageDismissed(m); }}>{m.text}</MessageBar>
                        </StackItem>))}
                    </>}
                    <StackItem>
                        <Stack horizontalAlign="end" horizontal tokens={{ childrenGap: 10 }}>
                            {this.props.buttons.map((b, i) => {
                                let button = undefined;
                                if (b.onClick || b.href) {
                                    const buttonProps: IButtonProps = { text: b.text, href: b.href, target: b.target, styles: b.styles };
                                    if (b.onClick) {
                                        buttonProps.onClick = (event) => { this._onButtonClicked(b, event); };
                                    }
                                    switch (b.type) {
                                        case SimpleDialogButtonType.Primary: {
                                            button = <PrimaryButton key={`button-${i}`} {...buttonProps} />;
                                            break;
                                        }
                                        case SimpleDialogButtonType.Default: {
                                            button = <DefaultButton key={`button-${i}`} {...buttonProps} />;
                                            break;
                                        }
                                    }
                                }
                                return button;
                            })}
                        </Stack>
                    </StackItem>
                </Stack>
            </Dialog>
        </>);
    }
}

/**
 * @summary Defines the properies for the ConfirmDialog.
 */
export interface IConfirmDialogProps extends IBaseDialogProps {
    /**
     * @summary The optional text to show on the confirm dialog button.
     * @description Default value is "Yes"
     */
    confirmButtonText?: string;
    /**
     * @summary The optional text to show on the reject dialog button.
     * @description Default value is "No"
     */
    rejectButtonText?: string;
    /**
     * @summary Function that is called when the confirmation is acknowledged.
     * @param result True if the confirm button was clicked. False if the reject button was clicked.
     */
    onClose: (result: boolean) => void;
}

/**
 * @summary A SimpleDialog wrapper that uses the specified title, message, onClose,
 * a reject button with the specified rejectButtonText or "Yes" if rejectButtonText is not provided,
 * and a confirm button with the specified confirmButtonText or "Yes" if confirmButtonText is not provided
 */
export class ConfirmDialog extends React.Component<IConfirmDialogProps, any> {
    /**
     * @summary Called when the confirm button is clicked.
     */
    private onConfirm = () => {
        this.props.onClose(true);
    };
    /**
     * @summary Called when the reject button is clicked.
     */
    private onReject = () => {
        this.props.onClose(false);
    };
    public render() {
        const { confirmButtonText, rejectButtonText, title, message } = this.props;
        return <SimpleDialog title={title} message={message} buttons={[
            {
                text: rejectButtonText || "No",
                type: SimpleDialogButtonType.Default,
                onClick: this.onReject
            },
            {
                text: confirmButtonText || "Yes",
                type: SimpleDialogButtonType.Primary,
                onClick: this.onConfirm
            }
        ]} />
    }
}

/**
 * @summary Defines the properies for the AlertDialog.
 */
export interface IAlertDialogProps extends IBaseDialogProps {
    /**
     * @summary The optional text to show on the dialog button.
     * @description Default value is "Okay"
     */
    buttonText?: string;

    /**
     * @summary Function that is called when the alert is acknowledged.
     */
    onClose: () => void;
}

/**
 * @summary A SimpleDialog wrapper that uses the specified title, message, onClose, and a single button with the specified buttonText or "Okay" if buttonText is not provided
 */
export class AlertDialog extends React.Component<IAlertDialogProps, any> {
    public render() {
        const { title, message, onClose, buttonText } = this.props;
        return <SimpleDialog title={title} message={message} buttons={[
            {
                text: buttonText || "Okay",
                type: SimpleDialogButtonType.Primary,
                onClick: onClose
            }
        ]} />
    }
}